import type { Project, ProjectDisplayQaIssues } from '../../types/project';

export enum Platform {
    ios = 1,
    android = 2,
    web = 4,
    windowsPhone = 8,
    other = 16,
}

export enum FileFormat {
    appleStrings = 10,
    iosSdk = 11,
    androidSdk = 12,
    appleStringsdict = 15,
    appleXliff = 20,
    applePlist = 25,
    androidResources = 30,
    resw = 40,
    resx = 50,
    javaProperties = 60,
    json = 70,
    jsonI18Next = 71,
    jsonStructured = 72,
    jsObject = 75,
    reactNativeJs = 76,
    gettextPo = 80,
    gettextPot = 90,
    symfonyXliff = 93,
    angularXliff = 94,
    php = 95,
    phpIni = 96,
    phpLaravel = 97,
    phpArray = 98,
    excel = 100,
    yaml = 110,
    yamlRuby = 111,
    stf = 120,
    csv = 130,
    ts = 140,
    html = 150,
    docx = 160,
    dfflineXliff = 170,
    multilingualExcel = 180,
    integrationData = 190,
    arb = 200,
    srt = 210,
    pptx = 220,
    idml = 230,
    flutterSdk = 240,
}

export enum PluralFormat {
    generic = 0,
    jsonString = 1,
    icu = 2,
    symfony = 3,
    array = 4,
    i18next = 5,
}

export enum PlaceholderFormat {
    printf = 0,
    ios = 1,
    icu = 2,
    net = 3,
    symfony = 4,
    i18n = 5,
    raw = 6,
}

export enum Indentation {
    default = 0, // No extra indentation processing
    oneSpace = 1,
    twoSpaces = 2,
    threeSpaces = 3,
    fourSpaces = 4,
    fiveSpaces = 5,
    sixSpaces = 6,
    sevenSpaces = 7,
    eightSpaces = 8,
    tab = 9,
}

export type ExportSort = 'first_added' | 'last_added' | 'last_updated' | 'a_z' | 'z_a';

export type ExportEmpty = 'empty' | 'skip' | 'base' | 'as_null';

export type ExportNullAs = 'empty' | 'null';

export type JavaPropertiesSeparator = 'auto' | '=' | ':';

export type JavaPropertiesEncoding = 'latin-1' | 'utf-8';

export type CustomTranslationStatusId = string;

export type ProjectId = string;

export type LanguageId = string;

export type Webhook = {
    url: string;
    active: boolean;
};

export type ReplaceLineBreaks = {
    [key in FileFormat]?: boolean;
};

export type ExportFormat =
    | Platform.ios
    | FileFormat.iosSdk
    | FileFormat.androidSdk
    | Platform.android
    | Platform.web
    | Platform.other;

export type Triggers = {
    triggersAmazons3: boolean;
    triggersGcs: boolean;
    triggersGithub: boolean;
    triggersGithubEnterprise: boolean;
    triggersGitlab: boolean;
    triggersBitbucket: boolean;
    triggersBitbucketEnterprise: boolean;
    triggersAzure: boolean;
};

export type BundleStructure = {
    iosZipStructure: string;
    androidZipStructure: string;
    arbStructure: string;
    webZipStructure: string;
    resxStructure: string;
    otherZipStructure: string;
    reactNativeStructure: string;
};

export type DirectoryPrefix = {
    iosDirectoryPrefix: string;
    androidDirectoryPrefix: string;
    arbDirectoryPrefix: string;
    webDirectoryPrefix: string;
    resxDirectoryPrefix: string;
    otherDirectoryPrefix: string;
    reactNativeDirectoryPrefix: string;
};

export type ExportConfig = Triggers &
    BundleStructure &
    DirectoryPrefix & {
        replaceLineBreaks: ReplaceLineBreaks;
        exportFormat: ExportFormat;
        fileFormat: FileFormat;
        switchIncludeAll: boolean;
        filenamesCustom: boolean;
        filenamesOriginal: boolean;
        directoryPrefix: string;
        includeTags: string; // "tag1,tag2,tag3"
        excludeTags: string; // "tag1,tag2,tag3"
        filterFilenames: readonly string[];
        exportEmpty: ExportEmpty;
        exportNullAs: ExportNullAs;
        exportSort: ExportSort;
        javaPropertiesEncoding: JavaPropertiesEncoding;
        javaPropertiesSeparator: JavaPropertiesSeparator;
        switchIcuNumeric: boolean;
        pluralFormat: PluralFormat | undefined;
        placeholderFormat: PlaceholderFormat | undefined;
        switchEscapePercent: boolean;
        indentation: Indentation | undefined;
        switchWebhooks: boolean;
        switchCompact: boolean;
        switchIncludeDescription: boolean;
        switchIncludeComments: boolean;
        switchNoLanguageFolders: boolean;
        switchDisableReferences: boolean;
        switchYamlIncludeRoot: boolean;
        jsonUnescapedSlashes: boolean;
        addNewlineEof: boolean;
        exportAll: boolean;
        exportGuntranslated: boolean;
        exportGtranslated: boolean;
        exportGproofread: boolean;
        exportGlastReviewed: boolean;
        exportGnonfuzzy: boolean;
        exportGnonhidden: boolean;
        filterRepositories: string; // "repo1,repo2,repo3"
        commitTemplate: string;
        customTranslationStatusIds: readonly CustomTranslationStatusId[];
        switchSourceFilenames: boolean; // TODO: This value still has to be handled
        exportPids: readonly ProjectId[];
        exportLangs: readonly string[];
        zipStructure: string;
        webhooks: readonly Webhook[];
        customIsoCodes: Partial<CustomIsoCodes> | null;
        defaultBitbucketReviewers: boolean;
    };

export interface ModuleProps {
    project: Project;
    exportConfig: ExportConfig;
    showReviewExport: boolean;
    indentationBlacklist: Partial<Record<FileFormat, readonly Indentation[]>>;
    indentationSelect: Partial<Record<Indentation, string>>;
    indentationDefaults: Partial<Record<FileFormat, Indentation>>;
    pluralFormatSelect: readonly {
        value: PluralFormat;
        text: string;
    }[];
    pluralFormatWhitelist: Partial<Record<FileFormat, readonly PluralFormat[]>>;
    pluralFormatDefaults: Partial<Record<FileFormat, PluralFormat>>;
    placeholderFormatSelect: readonly {
        value: PlaceholderFormat;
        text: string;
    }[];
    placeholderFormatWhitelist: Partial<Record<FileFormat, readonly PlaceholderFormat[]>>;
    placeholderFormatDefaults: Partial<Record<FileFormat, PlaceholderFormat>>;
    availableProjects: AvailableProjects;
    allKeysCount: number;
    integrations: Partial<Record<Integration, boolean>> | null;
    tags: readonly string[];
    availableFilenames: AvailableFileNames;
    baseLangIso: string;
    baseLangName: string;
    distinctPlatform: string;
    unassignedKeysPerPlatformFilename: { [name: string]: string };
    repositories: Repositories;
    customTranslationStatuses: {
        [projectId: string]: {
            projectTitle: string;
            statuses: readonly {
                id: CustomTranslationStatusId;
                title: string;
                color: string;
            }[];
        };
    };
    customTranslationStatusesAvailable: boolean;
}

export type DataRange = readonly {
    value: string;
    label: string;
    isChecked: boolean;
}[];

export type ExportSortOptions = readonly {
    value: ExportSort;
    label: string;
}[];

export type ExportEmptyOptions = readonly {
    value: ExportEmpty;
    label: string;
}[];

export type ExportNullAsOptions = readonly {
    value: ExportNullAs;
    label: string;
}[];

export type JavaPropertiesSeparatorOptions = readonly {
    value: JavaPropertiesSeparator;
    label: string;
}[];

export type JavaPropertiesEncodingOptions = readonly {
    value: JavaPropertiesEncoding;
    label: string;
}[];

export type EnabledTriggers = Record<keyof Triggers, boolean>;

export type Repositories = Partial<
    Record<
        'amazons3' | 'gcs' | 'github' | 'githubEnterprise' | 'gitlab' | 'bitbucket' | 'bitbucketEnterprise' | 'azure',
        readonly string[]
    >
>;

export type FileFormatMapping = Record<FileFormat, string>;

export type DownloadState = ModuleProps & {
    fileFormats: FileFormats;
    dataRange: DataRange;
    exportSortOptions: ExportSortOptions;
    exportEmptyOptions: ExportEmptyOptions;
    exportNullAsOptions: ExportNullAsOptions;
    javaPropertiesSeparatorOptions: JavaPropertiesSeparatorOptions;
    javaPropertiesEncodingOptions: JavaPropertiesEncodingOptions;
    enabledTriggers: EnabledTriggers;
    integrationsConfig: IntegrationsConfig;
    repositories: Repositories;
    fileFormatMapping: FileFormatMapping;
    projectBranchName: string | null;
    qaIssues: QaIssues;
};

export type Ext =
    | 'arb'
    | 'csv'
    | 'docx'
    | 'html'
    | 'ini'
    | 'js'
    | 'json'
    | 'php'
    | 'plist'
    | 'po'
    | 'properties'
    | 'resx'
    | 'srt'
    | 'stf'
    | 'strings'
    | 'ts'
    | 'xlf'
    | 'xliff'
    | 'xlsx'
    | 'xml'
    | 'yml';

export type Format = {
    platform: Platform;
    fallbackFilename: string;
    ext: Ext;
    name: string;
    value: FileFormat;
    label: string;
};

export type FileFormats = readonly {
    group: string;
    formats: readonly Format[];
}[];

export type AvailableFileNames = readonly { filename: string; device: Platform; format: FileFormat }[];

export type AvailableProjects = readonly { id: ProjectId; insertId: string; name: string }[];

export type CustomIsoCodes = Record<FileFormat, Record<LanguageId, string>>;

/* eslint-disable @typescript-eslint/naming-convention */
export type ExportData = {
    action: 'download_files';
    project_id: ProjectId;
    debug: 0 | 1;
    branch: null | string;
    config: {
        customIsoCodes: Partial<CustomIsoCodes>;
        replaceLineBreaks: ReplaceLineBreaks;
        export_format: ExportFormat;
        file_format: FileFormat;
        switch_include_all?: boolean;
        'filenames-custom'?: boolean;
        zip_structure: string;
        'filenames-original': boolean;
        directory_prefix: string;
        java_properties_encoding: JavaPropertiesEncoding;
        include_tags: string;
        exclude_tags: string;
        filter_filenames: string;
        export_empty: ExportEmpty;
        export_null_as: ExportNullAs;
        export_sort: ExportSort;
        java_properties_separator: JavaPropertiesSeparator;
        plural_format: PluralFormat | undefined;
        switch_icu_numeric: boolean;
        placeholder_format: PlaceholderFormat | undefined;
        switch_escape_percent: boolean;
        indentation: Partial<Record<FileFormat, Indentation>> | undefined;
        switch_webhooks: boolean;
        switch_compact?: boolean;
        switch_include_description?: boolean;
        switch_include_comments?: boolean;
        switch_no_language_folders?: boolean;
        switch_disable_references?: boolean;
        switch_yaml_include_root?: boolean;
        json_unescaped_slashes?: boolean;
        add_newline_eof?: boolean;
        export_all?: boolean;
        export_guntranslated?: boolean;
        export_gtranslated?: boolean;
        export_gproofread?: boolean;
        export_glast_reviewed?: boolean;
        export_gnonfuzzy?: boolean;
        export_gnonhidden?: boolean;
        triggers_amazons3: boolean;
        triggers_gcs: boolean;
        triggers_github: boolean;
        triggers_github_enterprise: boolean;
        triggers_gitlab: boolean;
        triggers_bitbucket: boolean;
        triggers_bitbucket_enterprise: boolean;
        triggers_azure: boolean;
        filter_repositories: string | string[];
        commit_template: string;
        custom_translation_status_ids: CustomTranslationStatusId;
        default_reviewers_bitbucket: boolean;
    };
};
/* eslint-enable @typescript-eslint/naming-convention */

export type DebugDownloadWindow = null | (Window & { debugDownloadData: ExportData; renderFiles: () => void });

declare global {
    interface Window {
        debugDownloadData: ExportData;
        debugDownload: undefined | DebugDownloadWindow;
    }
}

export type Integration =
    | 'amazons3'
    | 'gcs'
    | 'gitHub'
    | 'gitHubEnterprise'
    | 'gitLab'
    | 'bitBucket'
    | 'bitBuckEtenterprise'
    | 'azure';

export type IntegrationsConfig = Record<
    Integration,
    {
        title: string;
        description: string;
    }
>;

export type QaIssues = {
    issues: Partial<ProjectDisplayQaIssues>;
    total: number;
};
